import React, { useContext, useMemo } from "react"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { useRovingTabIndex } from "shared/lib/useRovingTabIndex"

import {
  ChipIndexContext,
  ChipListContext,
  useChipIndex
} from "./ChipListContext"
import "./styles/_chip-list.scss"
import { ChipListChipProps, ChipListListProps } from "./types/types"

const List = React.memo(
  React.forwardRef<HTMLDivElement, ChipListListProps>(
    (
      {
        children,
        disableRovingTabIndex = false,
        preventFocus = false,
        isRoundRobin = true,
        onFocusedChipChange,
        role = "toolbar",
        ...rest
      },
      forwardedRef
    ) => {
      const elementCount = React.Children.count(children)

      const { containerProps, getElementProps } = useRovingTabIndex<
        HTMLElement,
        HTMLDivElement
      >({
        elementCount,
        disableRovingTabIndex,
        preventFocus,
        isRoundRobin,
        onFocusedElementChange: onFocusedChipChange
      })

      const contextValue = useMemo(
        () => ({
          getElementProps: (index: number) => {
            const props = getElementProps(index)
            return {
              ...props,
              tabIndex: props.tabIndex ?? 0
            }
          }
        }),
        [getElementProps]
      )

      const { ref: containerRef, ...restContainerProps } = containerProps

      const mergedRef = useMergedRefs(containerRef, forwardedRef)

      if (elementCount === 0) {
        return null
      }

      const childrenWithIndex = React.Children.map(children, (child, index) => {
        const key = React.isValidElement(child) ? child.key : index
        return (
          <ChipIndexContext.Provider key={key} value={index}>
            {child}
          </ChipIndexContext.Provider>
        )
      })

      return (
        <ChipListContext.Provider value={contextValue}>
          <div
            ref={mergedRef}
            role={role}
            {...rest}
            {...restContainerProps}
            className={classNames("chip-list", rest.className)}
          >
            {childrenWithIndex}
          </div>
        </ChipListContext.Provider>
      )
    }
  )
)
List.displayName = "ChipListList"

function ChipComponent<
  ElementType extends "div" | "span" | "a" | "button" = "div"
>(
  { as, children, className, role, ...rest }: ChipListChipProps<ElementType>,
  forwardedRef: React.ForwardedRef<HTMLElement>
) {
  const context = useContext(ChipListContext)
  const index = useChipIndex()

  if (!context) {
    throw new Error("ChipList.Chip must be used within ChipList.List")
  }

  const { getElementProps } = context
  const rovingProps = exists(index) ? getElementProps(index) : null

  const mergedRef = useMergedRefs<HTMLElement>(rovingProps?.ref, forwardedRef)

  return React.createElement(
    as || "div",
    {
      ref: mergedRef,
      className: classNames("chip-list__chip", className),
      role,
      ...rest,
      ...rovingProps
    },
    children
  )
}

const Chip = React.memo(React.forwardRef(ChipComponent))
Chip.displayName = "ChipListChip"

export const ChipList = {
  List,
  Chip
}
